import React, { useEffect, useState } from "react"
import { Drawer, Modal } from "antd"
import * as com from "../Common"
import {
    parseAmount,
    statesUsedByOtherRows,
    invalidFeeRows,
    closingCostWindowError,
    compensationLimitError,
    compensationPreview,
    maxCompensationZeroesPay,
    MAX_COMPENSATION_ZERO_CONFIRM,
} from "./lenderConfig"
import "./LenderConfigDrawer.css"

const DEFAULT_LENDER_LOGO = "/images/lendersLogo/DefaultLender_logo.svg"
const RELATIONSHIP_KEYS = Object.keys(com.LenderRelationshhip)
const CORRESPONDENT_KEYS = ["NondedicatedCorrespondentLender", "DedicatedCorrespondentLender", "DirecttLender"]

const CloseIcon = ({ size = 16, color = "#71717A" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M6 6L18 18M18 6L6 18" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
)

const PlusIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M12 5V19M5 12H19" stroke="#325CEB" strokeWidth="2" strokeLinecap="round" />
    </svg>
)

const ChevronIcon = ({ className = "" }) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className={className}>
        <path d="M9 6L15 12L9 18" stroke="#325CEB" strokeWidth="2" strokeLinecap="round" />
    </svg>
)

// Values arrive from the API as numbers or nulls; render them as editable text.
const asText = (v) => (v === null || v === undefined ? "" : String(v))

// Loan amount the compensation preview starts from; the user can change it.
const PREVIEW_LOAN_AMOUNT = 400000

const usd = (n) => "$" + Math.round(n).toLocaleString("en-US")
const pct = (n) => (Math.round(n * 1000) / 1000).toFixed(3) + "%"

// The min/max compensation inputs keep digits and one dot only: the calculator
// cannot decode "1,500" or "$0", and an undecodable value takes the lender out
// of pricing altogether.
const dollarsOnly = (raw) => {
    const cleaned = raw.replace(/[^0-9.]/g, "")
    const dot = cleaned.indexOf(".")
    return dot < 0 ? cleaned : cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, "")
}

/**
 * One line under the compensation fields: what the lender pays on a loan of a
 * given size, and which limit, if any, changed it. Written for the case that
 * prompted it (production, 2026-11-04): max compensation "0.00" left over from
 * the old form silently priced 27 lender connections at $0 compensation.
 */
const CompensationPreview = ({ config, loanText, onLoanText }) => {
    const loan = parseAmount(loanText)
    const p = compensationPreview(config, loan)
    const zeroed = p.by === "max" && p.amount === 0 && p.uncapped > 0
    let outcome = null
    if (p.by === "max") {
        outcome = (
            <>
                {" "}→ capped at <b>{usd(p.amount)}</b> by max compensation
                {zeroed ? ". You earn nothing from this lender." : "."}
            </>
        )
    } else if (p.by === "min") {
        outcome = <> → raised to <b>{usd(p.amount)}</b> by min compensation.</>
    }
    return (
        <div className={`lcd-comp-preview ${zeroed ? "lcd-comp-preview-warn" : ""}`} data-testid="comp-preview">
            On a{" "}
            <span className="lcd-comp-preview-loan">
                $
                <input
                    aria-label="Preview loan amount"
                    value={loanText}
                    onChange={(e) => onLoanText(e.target.value.replace(/[^0-9,]/g, ""))}
                />
            </span>{" "}
            loan: {pct(p.percent)} ({usd((loan * p.percent) / 100)}) + {usd(p.flat)} flat ={" "}
            <b>{usd(p.uncapped)}</b>
            {outcome}
        </div>
    )
}

/**
 * The lender configuration drawer.
 *
 * `record` is a row from the lenders table and carries the same field handlers
 * the inline editor used, so editing writes straight through to
 * `record.config` exactly as before. Fee rows are edited here directly and
 * `onConfigChange` asks the page to re-render.
 *
 * Adjustments and promotions keep their full builders; they are folded away by
 * default because they are rarely touched and dominated the old layout.
 */
const LenderConfigDrawer = ({
    open,
    record,
    stateOptions = [],
    logoMap = {},
    dirty = false,
    onClose,
    onSave,
    onConfigChange,
    onAddPromotion,
    onAddAdjustment,
    formRef,
    onSubmit,
    validated,
}) => {
    // rows added in this session that have no states picked yet -- without this a
    // fresh override would be indistinguishable from the all-states default row
    const [newOverrides, setNewOverrides] = useState([])
    const [openPickers, setOpenPickers] = useState([])
    const [showErrors, setShowErrors] = useState(false)
    // a closing cost window that was already broken when the drawer opened is
    // shown straight away: it has been hiding this lender from every search
    const [showClosingCostError, setShowClosingCostError] = useState(false)
    const [foldOpen, setFoldOpen] = useState({ promotions: false, adjustments: false })
    // what the fee inputs currently show. config.lenderfees[].fee must stay a
    // number for the backend (LenderFee.Fee is float64), but a half-typed "0."
    // has no numeric form, so the raw text lives here while the field is edited.
    const [feeDrafts, setFeeDrafts] = useState({})
    const [previewLoan, setPreviewLoan] = useState(PREVIEW_LOAN_AMOUNT.toLocaleString("en-US"))

    useEffect(() => {
        if (open) {
            setNewOverrides([])
            setOpenPickers([])
            setShowErrors(false)
            const saved = (record && record.config) || {}
            setShowClosingCostError(
                closingCostWindowError(saved.minimumClosingCostPercentage, saved.maximumClosingCostPercentage) !== null
            )
            setFoldOpen({ promotions: false, adjustments: false })
            setFeeDrafts({})
        }
        // reset only when the drawer opens or switches lender, not on every edit
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, record && record.key])

    if (!record) return null

    const config = record.config || {}
    const fees = config.lenderfees || []

    const isOverrideRow = (fee, i) => (fee.states && fee.states.length > 0) || newOverrides.includes(i)
    const defaultIndex = fees.findIndex((f, i) => !isOverrideRow(f, i))

    const touch = () => {
        setShowErrors(false)
        onConfigChange()
    }

    const setFee = (i, patch) => {
        Object.assign(fees[i], patch)
        touch()
    }

    const addOverride = () => {
        fees.push({ fee: 0, feeFormat: "flat", states: [], mortgageTypes: [] })
        const i = fees.length - 1
        setNewOverrides((prev) => [...prev, i])
        setOpenPickers((prev) => [...prev, i])
        touch()
    }

    const removeFee = (i) => {
        fees.splice(i, 1)
        // indexes shift down once a row is gone
        const shift = (list) => list.filter((n) => n !== i).map((n) => (n > i ? n - 1 : n))
        setNewOverrides(shift)
        setOpenPickers(shift)
        setFeeDrafts((prev) => {
            const next = {}
            Object.keys(prev).forEach((k) => {
                const n = Number(k)
                if (n === i) return
                next[n > i ? n - 1 : n] = prev[k]
            })
            return next
        })
        touch()
    }

    const badFeeRows = invalidFeeRows(fees)
    const closingCostError = closingCostWindowError(
        config.minimumClosingCostPercentage,
        config.maximumClosingCostPercentage
    )

    const closingCostInvalid = showClosingCostError && closingCostError !== null
    // a min above the max (or a value the calculator cannot read) shows at once:
    // it is wrong whatever else is in the form
    const compError = compensationLimitError(config.minimumCompensation, config.maximumCompensation)

    // Save the way the button would have: onSave marks the lender edited, then
    // the form's submit handler posts it.
    const submitNow = () => {
        onSave()
        const form = formRef && formRef.current
        if (!form) return
        if (typeof form.requestSubmit === "function") form.requestSubmit()
        else form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }))
    }

    const handleSave = (e) => {
        if (badFeeRows.length > 0 || closingCostError || compError) {
            e.preventDefault()
            e.stopPropagation()
            setShowErrors(true)
            setShowClosingCostError(true)
            return
        }
        if (maxCompensationZeroesPay(config)) {
            // $0 is a legal limit, so ask instead of refusing
            e.preventDefault()
            e.stopPropagation()
            Modal.confirm({
                title: "Max compensation is $0",
                content: MAX_COMPENSATION_ZERO_CONFIRM,
                okText: "Save with $0",
                cancelText: "Go back",
                onOk: submitNow,
            })
            return
        }
        onSave(e)
    }

    const onFeeInput = (i, raw) => {
        const cleaned = raw.replace(/[^0-9.]/g, "")
        setFeeDrafts((prev) => ({ ...prev, [i]: cleaned }))
        setFee(i, { fee: parseAmount(cleaned) })
    }

    const feeAmountCell = (fee, i) => (
        <div className={`lcd-input lcd-fee-amount ${showErrors && badFeeRows.includes(i) ? "lcd-invalid" : ""}`}>
            <span className="lcd-affix">{fee.feeFormat === "percentage" ? "%" : "$"}</span>
            <input
                value={i in feeDrafts ? feeDrafts[i] : asText(fee.fee)}
                onChange={(e) => onFeeInput(i, e.target.value)}
            />
        </div>
    )

    const feeTypeCell = (fee, i) => (
        <select
            className="lcd-fee-type"
            value={fee.feeFormat || "flat"}
            onChange={(e) => setFee(i, { feeFormat: e.target.value })}
        >
            <option value="flat">Flat fee</option>
            <option value="percentage">Percent of loan</option>
        </select>
    )

    const statePicker = (fee, i) => {
        const taken = statesUsedByOtherRows(fees, i)
        return (
            <div className="lcd-state-picker">
                {stateOptions.map((st) => {
                    const on = (fee.states || []).includes(st.value)
                    const blocked = !on && taken.has(st.value)
                    return (
                        <span
                            key={st.value}
                            title={blocked ? st.label + " — already used by another fee" : st.label}
                            className={`lcd-state-option ${on ? "lcd-on" : ""} ${blocked ? "lcd-taken" : ""}`}
                            onClick={() => {
                                if (blocked) return
                                setFee(i, {
                                    states: on
                                        ? fee.states.filter((s) => s !== st.value)
                                        : [...(fee.states || []), st.value],
                                })
                            }}
                        >
                            {st.value}
                        </span>
                    )
                })}
            </div>
        )
    }

    const fold = (key, title, count, actionLabel, onAction, body) => (
        <div className="lcd-fold">
            <div className="lcd-fold-head" onClick={() => setFoldOpen({ ...foldOpen, [key]: !foldOpen[key] })}>
                <span className="lcd-fold-title">
                    <ChevronIcon className={`lcd-fold-arrow ${foldOpen[key] ? "lcd-open" : ""}`} />
                    {title}
                    <span className="lcd-fold-count">{count > 0 ? count + " configured" : "None configured"}</span>
                </span>
                <span
                    className="lcd-fold-action"
                    onClick={(e) => {
                        e.stopPropagation()
                        onAction()
                    }}
                >
                    {actionLabel}
                </span>
            </div>
            {foldOpen[key] && <div className="lcd-fold-body">{body}</div>}
        </div>
    )

    const logo = logoMap[record.name] || DEFAULT_LENDER_LOGO
    const isCorrespondent = CORRESPONDENT_KEYS.includes(config.correspondentLender)

    return (
        <Drawer
            open={open}
            onClose={onClose}
            placement="right"
            width={620}
            closable={false}
            maskClosable={false}
            destroyOnClose
            className="lcd-drawer"
            styles={{ body: { padding: 0 } }}
        >
            <form
                ref={formRef}
                onSubmit={onSubmit}
                noValidate
                className={validated ? "was-validated" : ""}
                style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}
            >
                <div className="lcd-head">
                    <div className="lcd-head-left">
                        <img src={logo} alt="" />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="lcd-name">{record.name}</div>
                            <div className="lcd-subtitle">Lender configuration</div>
                        </div>
                    </div>
                    <button type="button" className="lcd-close" onClick={onClose}>
                        <CloseIcon />
                    </button>
                </div>

                <div className="lcd-body">
                    <div className="lcd-section-title">Compensation</div>
                    <div className="lcd-grid-2">
                        <div>
                            <div className="lcd-field-label">Compensation point</div>
                            <div className="lcd-input">
                                <input value={asText(config.compensation)} onChange={record.onCompensation} />
                                <span className="lcd-affix">%</span>
                            </div>
                        </div>
                        <div>
                            <div className="lcd-field-label">Flat fee</div>
                            <div className="lcd-input">
                                <span className="lcd-affix">$</span>
                                <input value={asText(config.compensationFlatFee)} onChange={record.onCompensationFlatFee} />
                            </div>
                        </div>
                        <div>
                            <div className="lcd-field-label">
                                Min compensation <span className="lcd-optional">(optional)</span>
                            </div>
                            <div className={`lcd-input ${compError ? "lcd-invalid" : ""}`}>
                                <span className="lcd-affix">$</span>
                                <input
                                    placeholder="None"
                                    value={asText(config.minimumCompensation)}
                                    onChange={(e) => record.onMinCompensation({ target: { value: dollarsOnly(e.target.value) } })}
                                />
                            </div>
                        </div>
                        <div>
                            <div className="lcd-field-label">
                                Max compensation <span className="lcd-optional">(optional)</span>
                            </div>
                            <div className={`lcd-input ${compError ? "lcd-invalid" : ""}`}>
                                <span className="lcd-affix">$</span>
                                <input
                                    placeholder="None"
                                    value={asText(config.maximumCompensation)}
                                    onChange={(e) => record.onMaxCompensation({ target: { value: dollarsOnly(e.target.value) } })}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="lcd-section-note lcd-comp-note">Leave min or max blank for no limit.</div>
                    {compError && <div className="lcd-cc-error">{compError}</div>}
                    <CompensationPreview config={config} loanText={previewLoan} onLoanText={setPreviewLoan} />

                    <div className="lcd-rule" />

                    <div className="lcd-section-title">Closing cost limits</div>
                    <div className="lcd-grid-2">
                        <div>
                            <div className="lcd-field-label">
                                Min closing cost <span className="lcd-optional">(optional)</span>
                            </div>
                            <div className={`lcd-input ${closingCostInvalid ? "lcd-invalid" : ""}`}>
                                <input placeholder="No limit" value={asText(config.minimumClosingCostPercentage)} onChange={record.onMincost} />
                                <span className="lcd-affix">%</span>
                            </div>
                        </div>
                        <div>
                            <div className="lcd-field-label">
                                Max closing cost <span className="lcd-optional">(optional)</span>
                            </div>
                            <div className={`lcd-input ${closingCostInvalid ? "lcd-invalid" : ""}`}>
                                <input placeholder="No limit" value={asText(config.maximumClosingCostPercentage)} onChange={record.onMaxcost} />
                                <span className="lcd-affix">%</span>
                            </div>
                        </div>
                    </div>
                    {closingCostInvalid && <div className="lcd-cc-error">{closingCostError}</div>}

                    <div className="lcd-rule" />

                    <div className="lcd-section-title">Origination fee</div>
                    <div className="lcd-section-note">
                        One default fee applies everywhere. Add overrides to charge differently in specific states.
                    </div>
                    <div className="lcd-fee-box">
                        {defaultIndex >= 0 && (
                            <>
                                <div className="lcd-fee-row lcd-fee-row-default">
                                    <span className="lcd-chip-neutral">All states</span>
                                    <span className="lcd-fee-caption">Default fee</span>
                                    {feeTypeCell(fees[defaultIndex], defaultIndex)}
                                    {feeAmountCell(fees[defaultIndex], defaultIndex)}
                                </div>
                                {showErrors && badFeeRows.includes(defaultIndex) && (
                                    <div className="lcd-fee-error">Enter an origination fee greater than 0.</div>
                                )}
                            </>
                        )}

                        {fees.map((fee, i) => {
                            if (i === defaultIndex) return null
                            const pickerOpen = openPickers.includes(i)
                            return (
                                <div className="lcd-fee-override" key={i}>
                                    <div className="lcd-fee-row" style={{ padding: 0 }}>
                                        <div className="lcd-state-chips">
                                            {(fee.states || []).map((abbr) => (
                                                <span className="lcd-chip-state" key={abbr}>{abbr}</span>
                                            ))}
                                            <span
                                                className={`lcd-picker-btn ${pickerOpen ? "lcd-on" : ""}`}
                                                onClick={() =>
                                                    setOpenPickers((prev) =>
                                                        pickerOpen ? prev.filter((n) => n !== i) : [...prev, i]
                                                    )
                                                }
                                            >
                                                {pickerOpen ? "Done" : (fee.states || []).length ? "Edit states" : "Choose states"}
                                            </span>
                                        </div>
                                        {feeTypeCell(fee, i)}
                                        {feeAmountCell(fee, i)}
                                        <button type="button" className="lcd-icon-btn" title="Remove this fee" onClick={() => removeFee(i)}>
                                            <CloseIcon size={14} color="#B13A3A" />
                                        </button>
                                    </div>
                                    {showErrors && badFeeRows.includes(i) && (
                                        <div className="lcd-fee-error" style={{ padding: "8px 0 0" }}>
                                            Enter a fee greater than 0 for these states.
                                        </div>
                                    )}
                                    {pickerOpen && statePicker(fee, i)}
                                </div>
                            )
                        })}

                        <button type="button" className="lcd-add-row" onClick={addOverride}>
                            <PlusIcon />
                            {defaultIndex >= 0 ? "Add state-specific fee" : "Add a fee"}
                        </button>
                    </div>

                    <div className="lcd-rule" />

                    <div className="lcd-section-title">Relationship</div>
                    <div className={isCorrespondent ? "lcd-grid-2" : ""} style={isCorrespondent ? {} : { maxWidth: 280 }}>
                        <div>
                            <select
                                className="lcd-select"
                                value={config.correspondentLender || RELATIONSHIP_KEYS[0]}
                                onChange={record.onCorrespondent}
                            >
                                {RELATIONSHIP_KEYS.map((key) => (
                                    <option value={key} key={key}>
                                        {com.LenderRelationshhip[key].name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {isCorrespondent && (
                            <div>
                                <div className="lcd-field-label">
                                    Close over <span className="lcd-optional">(optional)</span>
                                </div>
                                <div className="lcd-input">
                                    <input
                                        placeholder="None"
                                        value={asText(config.closeover)}
                                        onChange={record.onCloseover}
                                    />
                                    <span className="lcd-affix">%</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="lcd-rule" />

                    {fold(
                        "promotions",
                        "Promotions",
                        (config.promotions || []).length,
                        "Add promotion",
                        () => onAddPromotion(record),
                        config.promotions != null ? record.displayEstablishedPromotions() : null
                    )}
                    {fold(
                        "adjustments",
                        "Adjustments",
                        (config.rules || []).length,
                        "Add adjustment",
                        () => onAddAdjustment(record),
                        config.rules != null ? record.displayEstablishedRules() : null
                    )}
                </div>

                <div className="lcd-foot">
                    {dirty ? (
                        <span className="lcd-dirty">
                            <span className="lcd-dirty-dot" />
                            Unsaved changes
                        </span>
                    ) : (
                        <span />
                    )}
                    <div className="lcd-foot-actions">
                        <button type="button" className="lcd-btn-secondary" onClick={onClose}>Cancel</button>
                        <button type="submit" className="lcd-btn-primary" onClick={handleSave}>Save changes</button>
                    </div>
                </div>
            </form>
        </Drawer>
    )
}

export default LenderConfigDrawer
